import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { postReq } from "../utils/request";
import { io, Socket } from "socket.io-client";
import { jwtDecode } from "jwt-decode";
import { getJwtToken, getCurrentUserId } from "../utils/token";
import { useUserPermissions } from "./UserPermissionsContext";
import { getReq } from "../utils/request";

/**
 * Web realtime context — owns a single Socket.IO connection scoped to
 * dispatch-side events (panic, operator positions). Notification inbox
 * has its own hook (useNotifications) and its own connection; we keep
 * them separate so each can evolve without coupling. Both are cheap.
 *
 * Consumers:
 *   - <RealtimePanicOverlay /> mounted at App level → screen-blocking
 *     modal when a `panic` event arrives.
 *   - useOperatorPositions(missionId) → live Map of latest fix per
 *     operator, fed by `operator_position` events scoped to a mission.
 */

export interface PanicAlert {
    legIncidentId: number | null;
    legId: number | null;
    itineraryId: number | null;
    missionId: number | null;
    missionRef: string | null;
    reporter: {
        userId: number;
        userDetailId: number;
        name: string;
        photoUrl: string | null;
        organizationId: number | null;
    };
    location: { lat: number; lng: number } | null;
    message: string;
    triggeredAt: string;
    /** Local-only — set when the dispatcher acknowledges this alert. */
    acknowledged?: boolean;
}

export interface OperatorPosition {
    userId: number;
    userDetailId: number;
    name: string;
    photoUrl: string | null;
    lat: number;
    lng: number;
    accuracy: number | null;
    heading: number | null;
    speed: number | null;
    /** Device battery 0–100. Optional — present only when the BE
     *  broadcast includes it (the persisted OperatorPositionLog always
     *  has it; older emit shapes omit it). Consumers render battery
     *  only when non-null. */
    batteryPercent?: number | null;
    missionId: number | null;
    /** Leg the operator is moving on. Was `itineraryId` against the
     *  renamed-old-Itinerary table; the column on the BE is now
     *  `leg_id`, and the broadcast carries `legId`. */
    legId: number | null;
    recordedAt: string;
}

/**
 * Leg checkpoint event. The mobile app splits the operational sequence
 * into three stages — Mark Team Arrival, Begin Movement, Complete Leg
 * — to match the three actual columns (`actualTeamArrivalDateTime`,
 * `actualDateTimeFrom`, `actualDateTimeTo`).
 *
 * **Naming history:** previously called `LegCheckpoint`; renamed
 * because the row this scopes to was always a leg (per-movement), not
 * an itinerary (phase grouping). The socket event name flipped from
 * `itinerary_checkpoint` to `leg_checkpoint` in the BE.
 *
 * Backwards-compat: legacy mobile builds may still emit `"in"` (treat
 * as arrive) or `"out"` (treat as complete). The badge renderer maps
 * both old and new values to the same three labels so a stale build
 * doesn't break the dispatcher view.
 */
export interface LegCheckpoint {
    legId: number;
    itineraryId: number | null;
    missionId: number | null;
    kind: "arrive" | "depart" | "complete" | "in" | "out";
    operator: { userId: number; userDetailId: number; name: string };
    /** Filled when the operator marks team arrival at the pickup. */
    actualTeamArrivalDateTime: string | null;
    actualDateTimeFrom: string | null;
    actualDateTimeTo: string | null;
}

/**
 * Buddy-check-overdue event: an operator missed their dead-man timer
 * confirmation. Persisted server-side (Phase 2.4) so the dispatcher who
 * loads the page mid-incident still sees it. The `acknowledged*` fields
 * are filled when a dispatcher hits the Acknowledge button.
 */
export interface BuddyOverdue {
    buddyCheckOverdueId: number;
    operator: {
        userId: number;
        userDetailId: number;
        name: string;
        photoUrl: string | null;
        missionId: number | null;
        itineraryId: number | null;
    };
    triggeredAt: string;
    intervalMinutes: number;
    lastAckAt: string | null;
    acknowledgedAt: string | null;
    acknowledgedBy: number | null;
}

/**
 * Pre-departure checklist submission, broadcast when the operator
 * completes the per-leg checklist on the mobile app. Drives the
 * "PRE-CHECKED" / "PRE-CHECK · GAPS" badge on the dispatcher's
 * itinerary card.
 */
export interface ChecklistSubmissionItem {
    label: string;
    checked: boolean;
    notes: string | null;
}
export interface ChecklistSubmission {
    /** The leg the operator pre-checked. Was `itineraryId` against
     *  the renamed-old-Itinerary table; matches the BE socket emit. */
    legId: number;
    itineraryId: number | null;
    missionId: number | null;
    operator: { userId: number; userDetailId: number; fullName: string; photoUrl: string | null };
    items: ChecklistSubmissionItem[];
    allChecked: boolean;
    notes: string | null;
    submittedAt: string;
}

/**
 * Incident report file event, broadcast when a mobile operator submits
 * a non-emergency incident report from the device. Drives the live
 * patch into the mission's per-leg Incidents sub-tab so dispatchers
 * see the new row without a manual refresh. `severity` is reserved for
 * future use (the entity carries no severity column today) but the
 * payload shape includes it so we don't have to ship a second BE
 * change when it gets added — values are nullable today.
 */
export interface IncidentFiled {
    legIncidentId: number | null;
    legId: number | null;
    missionId: number | null;
    /** Mission reference number / title, carried so the dispatcher modal can
     *  show a readable ref instead of "Mission #id". Null when unresolved
     *  (no leg id, or an older emitter). */
    missionRef: string | null;
    operatorUserId: number | null;
    /** Reporting operator's full display name, carried so the dispatcher modal
     *  can show a human name instead of "Operator #id". Null when unresolved
     *  (or an older emitter). */
    operatorName: string | null;
    incidentType: string | null;
    severity: string | null;
    filedAt: string;
    /** GPS fix where the incident was filed — drives a LIVE map pin on the
     *  dispatcher's leg view. Null for address-only incidents (the map
     *  geocodes the `address` instead); pass them through regardless. */
    latitude: number | null;
    longitude: number | null;
    /** Free-text location string — present for address-only incidents and
     *  also alongside coords. */
    address: string | null;
    /** The incident's display name + operator's note, carried so the live
     *  stub can render the pin's label/popup before the next page-mount
     *  fetch backfills the full record. */
    incidentName: string | null;
    description: string | null;
}

/**
 * Briefing-complete event, broadcast when an operator confirms they've
 * read the mission briefing on mobile. The BE flips every assigned
 * ItineraryTeamMember row in one shot and emits a single event —
 * consumers bump a per-mission revision counter and refetch the
 * lifecycle envelope (same pattern as missionPhaseRevision).
 */
export interface BriefingCompleted {
    missionId: number;
    itineraryTeamMemberId: number | null;
    operatorUserDetailId: number | null;
    briefingCompleteAt: string;
    rowsUpdated: number;
}

/**
 * SitRep filed event, broadcast when an operator submits a SitRep
 * (Situation Report) from the mobile NowCard. Drives the SitReps tab
 * live update — replacing the legacy 30s polling so critical SitReps
 * surface within ~1s. Body excerpt + GPS pin are included so the
 * dispatcher gets a hint before the tab reloads the full list.
 */
export interface SitRepFiled {
    sitRepId: number;
    missionId: number;
    legId: number | null;
    severity: string;
    body: string;
    latitude: number | null;
    longitude: number | null;
    operatorUserDetailId: number;
    createdAt: string;
}

/**
 * Live flight track fix, broadcast by the BE flight-track poller
 * (adsb.lol / OpenSky) for FlightInformation rows on active missions.
 * Keyed by flightInformationId — the latest fix wins. `lat`/`lng` are
 * null when the aircraft isn't currently being tracked (e.g. scheduled,
 * landed, or not found); consumers should only plot non-null fixes.
 */
export interface FlightTrackUpdate {
    flightInformationId: number;
    legId: number | null;
    missionId: number | null;
    trackStatus: string;
    lat: number | null;
    lng: number | null;
    headingDeg: number | null;
    groundSpeedKts: number | null;
    estimatedArrivalAt: string | null;
    delayMinutesEst: number | null;
    lastCheckedAt: string | null;
}

/**
 * Expense filed event, broadcast when an operator logs a leg expense
 * on mobile ("just paid 200 SAR for fuel"). Drives the Finance tab +
 * Itinerary tab live patch. `currency` is the 3-letter ISO code when
 * the BE could resolve it; `currencyId` is the lookup id and is the
 * authoritative reference.
 */
export interface ExpenseFiled {
    legExpenseId: number | null;
    legId: number;
    missionId: number | null;
    amount: number;
    currency: string | null;
    currencyId: number | null;
    operatorUserDetailId: number | null;
    createdAt: string;
}

/**
 * Mission Debrief filed event, broadcast when an operator submits or
 * edits their Debrief / AAR from mobile. Drives the Debrief tab + the
 * "X of Y debriefs filed" pill on Engagements via a per-mission
 * revision bump. Body excerpt (≤80 chars) is included so the
 * dispatcher gets a preview before re-fetching.
 */
export interface DebriefFiled {
    missionDebriefId: number;
    missionId: number;
    operatorUserDetailId: number;
    opsSummaryExcerpt: string;
    submittedAt: string;
}

/**
 * End-of-shift report, broadcast when the operator submits the
 * end-of-shift wizard. Drives the Shift Reports tab on the mission.
 */
export interface ShiftReport {
    shiftReportId: number;
    missionId: number | null;
    legId: number | null;
    operator: { userId: number; userDetailId: number; fullName: string; photoUrl: string | null };
    hoursWorked: number | null;
    mileage: number | null;
    vehicleReturnState: string | null;
    equipmentReturned: boolean | null;
    notableEvents: string | null;
    notes: string | null;
    submittedAt: string;
    /** STANDBY-only stand-down moment, stored inside the shift report's
     *  `workflow_payload` JSONB (key `standDownAt`). Null for non-STANDBY
     *  reports or when the operator never stood down. Dispatchers can
     *  correct it via the override endpoint. */
    standDownAt?: string | null;
}

/**
 * World-incident alert signal, broadcast by the BE ingest pipeline when new
 * alertable world events land (and which active missions they're near). The
 * payload carries no incident bodies — consumers refetch the incident search
 * + show a transient "N new events" pill. `missionIds` lists the active
 * missions the new events are near (may be empty for global-only alerts).
 */
export interface WorldIncidentAlert {
    kind: "alerts";
    missionIds: number[];
    count: number;
}

/**
 * One row in the per-entity presence list — represents a distinct user
 * (deduped server-side across multi-tab sessions). state escalates to
 * "editing" when any of that user's tabs has the form dirty.
 */
export interface PresenceUser {
    userId: number;
    fullName: string | null;
    photoUrl: string | null;
    state: "viewing" | "editing";
}

/**
 * One realtime comment event (`comment_added` / `comment_updated` /
 * `comment_deleted`). `comment` is the full CommentDTO the BE broadcast —
 * typed loosely here because the thread component owns the DTO shape.
 * `missionId` is set for itinerary / leg comments so the mission-level
 * rollup thread can pick them up. `seq` is a session-monotonic counter
 * consumers use to skip events they already applied.
 */
export interface CommentFeedEvent {
    seq: number;
    type: "added" | "updated" | "deleted";
    comment: { commentId: number; [key: string]: any };
    parentType: string;
    parentId: number;
    missionId: number | null;
    at: string;
}

/** Live socket state: never connected yet, connected, dropped and retrying, or retries exhausted. */
export type RealtimeStatus = "connecting" | "live" | "reconnecting" | "offline";

interface RealtimeContextValue {
    /** Active (un-acknowledged) panic alerts, FIFO. Most recent first. */
    panicQueue: PanicAlert[];
    /** Pop the head of the panic queue (after the dispatcher acks). */
    acknowledgePanic: (legIncidentId: number | null) => void;
    /** Active (un-acknowledged) incident-filed alerts, most recent first.
     *  Mirrors `panicQueue` — drives the screen-blocking
     *  <RealtimeIncidentOverlay /> so a dispatcher can't miss an
     *  operator-filed incident. The same `incident_filed` event ALSO
     *  patches `legIncidents` (the live leg sub-tab row); this queue is
     *  the alert surface, that map is the inline-row surface. */
    incidentQueue: IncidentFiled[];
    /** Pop the head of the incident queue (after the dispatcher acks). */
    acknowledgeIncident: (legIncidentId: number | null) => void;
    /** Non-destructive backlog of panic events received this session,
     *  newest first, bounded. Unlike `panicQueue`, acknowledging an
     *  alert does NOT remove it from here — map layers keep plotting
     *  the pin after the overlay modal is dismissed. Read via
     *  `usePanicEventsForMission(missionId)`. */
    panicEvents: PanicAlert[];
    /** Bounded per-mission backlog of full `sitrep_filed` payloads
     *  (incl. lat/lng + severity), newest first. Complements the
     *  id-only `sitRepsByMission` diagnostics list — this one carries
     *  the data the map's SitRep pins render from. Read via
     *  `useSitRepsForMission(missionId)`. */
    sitRepEvents: Map<number, SitRepFiled[]>;
    /** Map of flightInformationId → latest `flight_track_updated`
     *  fix. Read via `useFlightTracks(missionId)`. */
    flightTracks: Map<number, FlightTrackUpdate>;
    /** Map of userDetailId → latest fix; unscoped — consumers filter by mission. */
    operatorPositions: Map<number, OperatorPosition>;
    /** Map of itineraryId → latest checkpoint payload. */
    legCheckpoints: Map<number, LegCheckpoint>;
    /** Map of buddyCheckOverdueId → row. Includes both the seed (loaded
     *  on mount via GET /mobile/buddy-check-overdue/active) and any
     *  events that arrive over the socket while the page is open. */
    buddyOverdues: Map<number, BuddyOverdue>;
    /** Map keyed by `${itineraryId}:${userDetailId}` → that operator's
     *  latest checklist submission. A leg with multiple assigned operators
     *  has multiple entries — the consumer hook below filters/aggregates. */
    checklistSubmissions: Map<string, ChecklistSubmission>;
    /** Map of shiftReportId → submitted shift report. Filtered to a
     *  specific mission via `useShiftReportsForMission` consumer hook. */
    shiftReports: Map<number, ShiftReport>;
    /** Monotonic revision counter per missionId — bumped whenever any
     *  `risk_assessment_updated` or `risk_assessment_approved` socket
     *  event fires for that mission. Consumers subscribe via
     *  `useRiskAssessmentRevision(missionId)` and refetch when it
     *  changes. Counter-based (not a timestamp) so equality checks
     *  inside React's effect deps stay simple. */
    riskAssessmentRevision: Map<number, number>;
    /** Same pattern for mission lifecycle phase changes. Bumped on
     *  every `mission_phase_changed` event so the Mission detail page,
     *  the mission list, and the legs list refetch their state. */
    missionPhaseRevision: Map<number, number>;
    /** Global counter bumped on EVERY `mission_phase_changed` event,
     *  regardless of which mission. Mission lists and dashboards
     *  subscribe to this so they refetch whenever any mission moves
     *  phase — the list rendering doesn't know in advance which
     *  missionIds it cares about. */
    missionsListTick: number;
    /** Global counter bumped on every `mission_plan_changed` event — a
     *  movement's plan fields (times / type / addresses) were edited (e.g.
     *  from the Ops Board's inline cells). List/board pages subscribe so a
     *  colleague's plan edit shows up without a manual reload. */
    missionPlanTick: number;
    /** Per-mission counter bumped whenever an operator engagement
     *  transitions (`operator_engagement_changed`). The legacy name
     *  is preserved for backwards compat — the underlying socket
     *  event is the consolidated Wave 4a engagement stream that
     *  superseded the dropped `mission_check_in_recorded` event. */
    missionCheckInRevision: Map<number, number>;
    /** Map of legIncidentId → incident-filed payload. Patched in by
     *  `incident_filed` socket events; the LegIncidentList consumer
     *  layers this on top of the page-mount fetch via
     *  `useIncidentsForLeg(legId)`. */
    legIncidents: Map<number, IncidentFiled>;
    /** Per-mission counter bumped on every `briefing_completed` event.
     *  Subscribers refetch the mission lifecycle envelope so the
     *  "X of Y operators briefed" pill updates the instant the operator
     *  hits "I've read it" on mobile. */
    briefingRevision: Map<number, number>;
    /** Map of missionId → list of SitRep IDs that arrived live since
     *  the page mounted. Paired with `sitRepRevision` (below) so the
     *  SitReps tab refetches the bounded list endpoint on every push.
     *  The id list isn't load-bearing — it's mainly for debugging /
     *  burst detection — but keeping it lets the consumer skip a
     *  refetch when nothing new arrived. */
    sitRepsByMission: Map<number, number[]>;
    /** Per-mission counter bumped on every `sitrep_filed` event. The
     *  SitReps tab uses this as a `useEffect` dep instead of polling. */
    sitRepRevision: Map<number, number>;
    /** Map of legExpenseId → expense-filed payload. Patched in by
     *  `expense_filed` events; the Finance/Itinerary consumers layer
     *  this on top of their page-mount fetch via
     *  `useExpensesForLeg(legId)`. */
    legExpenses: Map<number, ExpenseFiled>;
    /** Per-mission counter bumped on every `debrief_filed` event. The
     *  Debrief tab + Engagements "X of Y debriefs" pill refetch on
     *  bump. */
    debriefRevision: Map<number, number>;
    /** Monotonic counter bumped on every `world_incident` (alerts) socket
     *  event. The Risk Console subscribes to it as a `useEffect` dep to
     *  refetch incidents; the count/missionIds carry the latest pill data. */
    worldIncidentTick: number;
    /** The most recent `world_incident` alert payload (count + missionIds),
     *  or null until one arrives this session. Drives the transient "N new
     *  events" pill on the Risk Console map. */
    lastWorldIncidentAlert: WorldIncidentAlert | null;
    /** Mark a buddy-overdue row as acknowledged locally — caller should
     *  also fire POST /mobile/buddy-check-overdue/:id/acknowledge so the
     *  BE persists it; this just removes the row from the live UI. */
    acknowledgeBuddyOverdue: (buddyCheckOverdueId: number) => void;
    /** Map keyed by `${type}:${id}` → list of users currently in that entity room. */
    entityPresence: Map<string, PresenceUser[]>;
    /** Monotonic revision per entity room (`${type}:${id}`), bumped on every
     *  `entity:section-updated` push from ANOTHER user (own saves are
     *  echo-guarded out). Collaborative-edit signal — a detail page watches it
     *  via `useEntitySectionRevision(type, id)` and refetches when it changes.
     *  Push-INVALIDATE: no record body rides the socket; the refetch goes
     *  through the normal permission-checked GET. */
    entitySectionRevision: Map<string, number>;
    /** Send the connection-control events for an entity room. */
    entityJoin: (type: string, id: number) => void;
    entityLeave: (type: string, id: number) => void;
    entityHeartbeat: (type: string, id: number) => void;
    entitySetEditing: (type: string, id: number, isEditing: boolean) => void;
    /** Bounded session backlog of comment socket events, newest first.
     *  Read via `useCommentsFeed(parentType, parentId, rollupMissionId?)`. */
    commentEvents: CommentFeedEvent[];
    /** Bumped on every socket `connect` (initial + reconnects). Threads use
     *  it as a useEffect dep to catch up with `?after=<lastCommentId>`. */
    realtimeConnectTick: number;
    /** Where the live socket stands, for the shell's status bar. */
    realtimeStatus: RealtimeStatus;
    /**
     * Ref-counted entity-room membership for secondary consumers (the
     * comments thread). Joins the `entity:{type}:{id}` room only if nothing
     * else already has, and the returned release function leaves it only
     * when no other holder (page banner via `entityJoin`, or another
     * ensure) still needs it — so closing a comments drawer never kicks the
     * page's presence banner out of its room.
     */
    ensureEntityRoom: (type: string, id: number) => () => void;
}

const noop = () => { /* noop default */ };
const noopRelease = () => noop;

/** How many panic events the session backlog keeps (newest first). */
const PANIC_BACKLOG_MAX = 50;
/** How many SitRep payloads are kept per mission (newest first). */
const SITREP_BACKLOG_MAX = 50;
/** How many comment events the session backlog keeps (newest first). */
const COMMENT_BACKLOG_MAX = 200;

const RealtimeContext = createContext<RealtimeContextValue>({
    panicQueue: [],
    acknowledgePanic: noop,
    incidentQueue: [],
    acknowledgeIncident: noop,
    panicEvents: [],
    sitRepEvents: new Map(),
    flightTracks: new Map(),
    operatorPositions: new Map(),
    legCheckpoints: new Map(),
    buddyOverdues: new Map(),
    checklistSubmissions: new Map(),
    shiftReports: new Map(),
    riskAssessmentRevision: new Map(),
    missionPhaseRevision: new Map(),
    missionsListTick: 0,
    missionPlanTick: 0,
    missionCheckInRevision: new Map(),
    legIncidents: new Map(),
    briefingRevision: new Map(),
    sitRepsByMission: new Map(),
    sitRepRevision: new Map(),
    legExpenses: new Map(),
    debriefRevision: new Map(),
    worldIncidentTick: 0,
    lastWorldIncidentAlert: null,
    acknowledgeBuddyOverdue: noop,
    entityPresence: new Map(),
    entitySectionRevision: new Map(),
    entityJoin: noop,
    entityLeave: noop,
    entityHeartbeat: noop,
    entitySetEditing: noop,
    commentEvents: [],
    realtimeConnectTick: 0,
    realtimeStatus: "connecting",
    ensureEntityRoom: noopRelease,
});

export const RealtimeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    // Re-run the connect effect when the logged-in user changes: after 2FA
    // the app navigates in without a reload, and a mount-only effect would
    // leave the socket unconnected until the next manual refresh.
    const { currentUser } = useUserPermissions();
    const [panicQueue, setPanicQueue] = useState<PanicAlert[]>([]);
    const [incidentQueue, setIncidentQueue] = useState<IncidentFiled[]>([]);
    const [panicEvents, setPanicEvents] = useState<PanicAlert[]>([]);
    const [sitRepEvents, setSitRepEvents] = useState<Map<number, SitRepFiled[]>>(new Map());
    const [flightTracks, setFlightTracks] = useState<Map<number, FlightTrackUpdate>>(new Map());
    const [operatorPositions, setOperatorPositions] = useState<Map<number, OperatorPosition>>(new Map());
    const [legCheckpoints, setLegCheckpoints] = useState<Map<number, LegCheckpoint>>(new Map());
    const [buddyOverdues, setBuddyOverdues] = useState<Map<number, BuddyOverdue>>(new Map());
    const [checklistSubmissions, setChecklistSubmissions] = useState<Map<string, ChecklistSubmission>>(new Map());
    const [shiftReports, setShiftReports] = useState<Map<number, ShiftReport>>(new Map());
    const [riskAssessmentRevision, setRiskAssessmentRevision] = useState<Map<number, number>>(new Map());
    const [missionPhaseRevision, setMissionPhaseRevision] = useState<Map<number, number>>(new Map());
    const [missionsListTick, setMissionsListTick] = useState<number>(0);
    const [missionPlanTick, setMissionPlanTick] = useState<number>(0);
    const [missionCheckInRevision, setMissionCheckInRevision] = useState<Map<number, number>>(new Map());
    const [legIncidents, setLegIncidents] = useState<Map<number, IncidentFiled>>(new Map());
    const [briefingRevision, setBriefingRevision] = useState<Map<number, number>>(new Map());
    const [sitRepsByMission, setSitRepsByMission] = useState<Map<number, number[]>>(new Map());
    const [sitRepRevision, setSitRepRevision] = useState<Map<number, number>>(new Map());
    const [legExpenses, setLegExpenses] = useState<Map<number, ExpenseFiled>>(new Map());
    const [debriefRevision, setDebriefRevision] = useState<Map<number, number>>(new Map());
    const [worldIncidentTick, setWorldIncidentTick] = useState<number>(0);
    const [lastWorldIncidentAlert, setLastWorldIncidentAlert] = useState<WorldIncidentAlert | null>(null);
    const [entityPresence, setEntityPresence] = useState<Map<string, PresenceUser[]>>(new Map());
    const [entitySectionRevision, setEntitySectionRevision] = useState<Map<string, number>>(new Map());
    const [commentEvents, setCommentEvents] = useState<CommentFeedEvent[]>([]);
    const [realtimeConnectTick, setRealtimeConnectTick] = useState<number>(0);
    const [realtimeStatus, setRealtimeStatus] = useState<RealtimeStatus>("connecting");
    const commentSeqRef = useRef(0);
    const socketRef = useRef<Socket | null>(null);
    /** Rooms joined explicitly through `entityJoin` (page-level banners). */
    const explicitJoinsRef = useRef<Set<string>>(new Set());
    /** Holder count per room for `ensureEntityRoom`. */
    const ensuredRoomsRef = useRef<Map<string, number>>(new Map());
    /**
     * Tracks which entity rooms the user has actively joined. Used to
     * re-emit entity:join after a socket reconnect — without this, every
     * dropped connection (proxy timeout, network blip, tab background)
     * silently kicks the user out of every room with no way back in until
     * they navigate the page.
     */
    const activeEntityRoomsRef = useRef<Set<string>>(new Set());
    /**
     * Mirrors the editing state per active room so we can also restore it
     * on reconnect. Without this, a user who was "editing" gets reset to
     * "viewing" on every reconnect.
     */
    const entityEditingRef = useRef<Map<string, boolean>>(new Map());

    /**
     * Acknowledge a panic alert — "I have this".
     *
     * Clears the overlay immediately, then PERSISTS the acknowledgement. It used
     * to do only the former: nothing was written down, so after a refresh there
     * was no record, and every other dispatcher's screen kept showing the alert
     * with no way to know a colleague had already responded. The server also
     * broadcasts `panic_acknowledged`, which clears the overlay everywhere else.
     *
     * The local clear is deliberately not rolled back if the request fails —
     * re-raising a screen-blocking alert the dispatcher has already responded to
     * would be worse than the missing record, and the failure is surfaced as a
     * toast so it can be retried from the incident itself.
     */
    const acknowledgePanic = useCallback((legIncidentId: number | null) => {
        setPanicQueue((q) => q.filter((p) => p.legIncidentId !== legIncidentId));
        if (!legIncidentId) return;
        void (async () => {
            try {
                const response = await postReq(`missions/panic/${legIncidentId}/acknowledge`, {});
                if (response.status !== 200) {
                    toast.error("Panic acknowledged on your screen, but it could not be recorded — tell the team directly.");
                }
            } catch {
                toast.error("Panic acknowledged on your screen, but it could not be recorded — tell the team directly.");
            }
        })();
    }, []);

    const acknowledgeIncident = useCallback((legIncidentId: number | null) => {
        setIncidentQueue((q) => q.filter((i) => i.legIncidentId !== legIncidentId));
    }, []);

    const acknowledgeBuddyOverdue = useCallback((buddyCheckOverdueId: number) => {
        // Local optimistic remove so the banner clears immediately. Caller is
        // expected to fire POST /mobile/buddy-check-overdue/:id/acknowledge to
        // persist on the BE.
        setBuddyOverdues((prev) => {
            const next = new Map(prev);
            next.delete(buddyCheckOverdueId);
            return next;
        });
    }, []);

    const entityJoin = useCallback((type: string, id: number) => {
        activeEntityRoomsRef.current.add(`${type}:${id}`);
        explicitJoinsRef.current.add(`${type}:${id}`);
        socketRef.current?.emit("entity:join", { type, id });
    }, []);

    const entityLeave = useCallback((type: string, id: number) => {
        activeEntityRoomsRef.current.delete(`${type}:${id}`);
        explicitJoinsRef.current.delete(`${type}:${id}`);
        entityEditingRef.current.delete(`${type}:${id}`);
        socketRef.current?.emit("entity:leave", { type, id });
        // Drop the local entry immediately so the banner doesn't briefly
        // include stale data while waiting for the server's broadcast.
        setEntityPresence((prev) => {
            const next = new Map(prev);
            next.delete(`${type}:${id}`);
            return next;
        });
    }, []);

    const entityHeartbeat = useCallback((type: string, id: number) => {
        socketRef.current?.emit("entity:heartbeat", { type, id });
    }, []);

    const entitySetEditing = useCallback((type: string, id: number, isEditing: boolean) => {
        entityEditingRef.current.set(`${type}:${id}`, isEditing);
        socketRef.current?.emit("entity:editing", { type, id, isEditing });
    }, []);

    const ensureEntityRoom = useCallback((type: string, id: number) => {
        const key = `${type}:${id}`;
        const holders = ensuredRoomsRef.current.get(key) ?? 0;
        ensuredRoomsRef.current.set(key, holders + 1);
        if (!activeEntityRoomsRef.current.has(key)) {
            // Recorded in the active set so the reconnect handler re-joins it.
            activeEntityRoomsRef.current.add(key);
            socketRef.current?.emit("entity:join", { type, id });
        }
        let released = false;
        return () => {
            if (released) return;
            released = true;
            const remaining = (ensuredRoomsRef.current.get(key) ?? 1) - 1;
            if (remaining > 0) {
                ensuredRoomsRef.current.set(key, remaining);
                return;
            }
            ensuredRoomsRef.current.delete(key);
            // A page-level banner still owns the room — leave it alone.
            if (explicitJoinsRef.current.has(key)) return;
            activeEntityRoomsRef.current.delete(key);
            socketRef.current?.emit("entity:leave", { type, id });
        };
    }, []);

    useEffect(() => {
        const token = getJwtToken();
        if (!token) return;
        // Only connect on a FULL session token. The pre-2FA token (issued
        // between password and code entry) has no pages claim and fails every
        // permission-checked call — connecting with it got the buddy-overdue
        // seed GET 403'd, which bounced a refreshed /2fa page to /denied.
        try {
            if (!(jwtDecode(token) as any)?.pages) return;
        } catch {
            return;
        }

        const apiUrl = process.env.REACT_APP_API_ENDPOINT?.replace("/api/", "").replace("/api", "") || "";
        const socket = io(apiUrl, {
            // A function, so every (re)connect sends the CURRENT token. The
            // access token rotates every 15 min; a static one made any reconnect
            // after that (network blip, sleep, backend deploy) fail the
            // handshake, and live panic/buddy alerts stopped until a reload.
            auth: (cb: (data: { token: string | null }) => void) => cb({ token: getJwtToken() }),
            transports: ["websocket", "polling"],
            reconnection: true,
            reconnectionDelay: 2000,
            reconnectionAttempts: 10,
        });
        socketRef.current = socket;

        // Connection state for the status bar. A failed first attempt stays
        // "connecting"; only a drop after being live counts as reconnecting,
        // and the manager giving up (reconnectionAttempts) means offline.
        socket.on("connect", () => setRealtimeStatus("live"));
        socket.on("disconnect", () => setRealtimeStatus("reconnecting"));
        // Optional chain: test doubles of the socket carry no Manager.
        socket.io?.on("reconnect_failed", () => setRealtimeStatus("offline"));

        socket.on("panic", (alert: PanicAlert) => {
            // De-dupe by incident id so reconnects don't re-stack the same alert.
            setPanicQueue((q) => {
                if (alert.legIncidentId != null && q.some((p) => p.legIncidentId === alert.legIncidentId)) {
                    return q;
                }
                return [alert, ...q];
            });
            // Session backlog for the map's panic-pin layer — survives the
            // dispatcher acknowledging the overlay modal. Same de-dupe by
            // incident id (a reconnect re-emit replaces the prior entry),
            // bounded so a pathological burst can't grow unbounded.
            setPanicEvents((prev) => {
                const withoutDupe = alert.legIncidentId != null
                    ? prev.filter((p) => p.legIncidentId !== alert.legIncidentId)
                    : prev;
                return [alert, ...withoutDupe].slice(0, PANIC_BACKLOG_MAX);
            });
        });

        /**
         * Another dispatcher took this one. Clear the screen-blocking overlay
         * here too — previously every dispatcher had to dismiss the same alert
         * independently, with no indication anyone had responded.
         *
         * Only the alert QUEUE is cleared; the session backlog is left intact so
         * the map keeps plotting the panic pin.
         */
        socket.on("panic_acknowledged", (ack: { legIncidentId: number }) => {
            setPanicQueue((q) => q.filter((p) => p.legIncidentId !== ack.legIncidentId));
        });

        socket.on("operator_position", (pos: OperatorPosition) => {
            // Replace the entry for this user — Map keeps lookup O(1) for
            // the InteractiveMap consumer.
            setOperatorPositions((prev) => {
                const next = new Map(prev);
                next.set(pos.userDetailId, pos);
                return next;
            });
        });

        socket.on("leg_checkpoint", (cp: LegCheckpoint) => {
            setLegCheckpoints((prev) => {
                const next = new Map(prev);
                next.set(cp.legId, cp);
                return next;
            });
        });

        socket.on("buddy_check_overdue", (event: BuddyOverdue) => {
            // Skip already-acked rows in case the BE re-emits during a
            // reconnect — the seed call covers historical state.
            if (event.acknowledgedAt) return;
            setBuddyOverdues((prev) => {
                const next = new Map(prev);
                next.set(event.buddyCheckOverdueId, event);
                return next;
            });
        });

        socket.on("checklist_submitted", (event: ChecklistSubmission) => {
            // Keyed by `(legId, operator)` so multiple deployed operators
            // on the same leg each have their own row. A re-submission
            // by the same operator overwrites their prior entry, but
            // never blows away another operator's submission.
            setChecklistSubmissions((prev) => {
                const next = new Map(prev);
                const key = `${event.legId}:${event.operator?.userDetailId ?? "unknown"}`;
                next.set(key, event);
                return next;
            });
        });

        socket.on("shift_closed", (event: ShiftReport) => {
            setShiftReports((prev) => {
                const next = new Map(prev);
                next.set(event.shiftReportId, event);
                return next;
            });
        });

        // Risk-assessment edits + approvals: don't carry payload state
        // (the tree is large; clients refetch on signal instead). Just
        // bump a per-mission revision counter — any open RiskAssessment
        // tab subscribing via `useRiskAssessmentRevision(missionId)`
        // re-runs its fetch on change.
        const bumpRiskAssessmentRevision = (payload: { missionId?: number }) => {
            if (typeof payload?.missionId !== "number") return;
            setRiskAssessmentRevision((prev) => {
                const next = new Map(prev);
                next.set(payload.missionId!, (prev.get(payload.missionId!) ?? 0) + 1);
                return next;
            });
        };
        socket.on("risk_assessment_updated", bumpRiskAssessmentRevision);
        socket.on("risk_assessment_approved", bumpRiskAssessmentRevision);

        // Mission lifecycle phase changes — same revision-counter
        // pattern. Web and mobile both subscribe; the panel + the
        // legacy status dropdown both refetch on bump. `missionsListTick`
        // is the unscoped counter for list pages that don't know
        // up-front which missionIds they're displaying.
        socket.on("mission_phase_changed", (payload: { missionId?: number; from?: string; to?: string }) => {
            if (typeof payload?.missionId !== "number") return;
            setMissionPhaseRevision((prev) => {
                const next = new Map(prev);
                next.set(payload.missionId!, (prev.get(payload.missionId!) ?? 0) + 1);
                return next;
            });
            setMissionsListTick((prev) => prev + 1);
        });

        // A movement's PLAN fields changed (times / type / addresses — the
        // Ops Board inline edits emit this). Unscoped tick: boards and lists
        // don't know up-front which missions they show.
        socket.on("mission_plan_changed", () => {
            setMissionPlanTick((prev) => prev + 1);
        });

        // Operator engagement transitioned (assigned / checked_in /
        // in_progress / checked_out / completed / cancelled / no_show).
        // Bumps the per-mission revision so the dispatcher's
        // EngagementsTab + lifecycle panel + Cycle Health widgets
        // refresh without manual reload. Replaces the legacy
        // `mission_check_in_recorded` event from the dropped
        // MissionCheckIn table.
        socket.on("operator_engagement_changed", (payload: { missionId?: number }) => {
            if (typeof payload?.missionId !== "number") return;
            setMissionCheckInRevision((prev) => {
                const next = new Map(prev);
                next.set(payload.missionId!, (prev.get(payload.missionId!) ?? 0) + 1);
                return next;
            });
        });

        // Incident reports — patch a map keyed by legIncidentId. The
        // LegIncidentList consumer (via `useIncidentsForLeg`) layers
        // live rows on top of the page-mount fetch so the new row
        // appears without a manual refresh. Re-firing under the same
        // id (extremely unlikely — incidents don't edit through this
        // channel) overwrites the prior entry.
        socket.on("incident_filed", (event: IncidentFiled) => {
            if (event.legIncidentId == null) return;
            // Normalize the coords/address/name/description so a partial
            // payload (older emitter) still stores well-typed nulls and
            // the map-pin / overlay consumers can rely on the shape.
            const normalized: IncidentFiled = {
                ...event,
                missionRef: event.missionRef ?? null,
                operatorName: event.operatorName ?? null,
                latitude: event.latitude ?? null,
                longitude: event.longitude ?? null,
                address: event.address ?? null,
                incidentName: event.incidentName ?? null,
                description: event.description ?? null,
            };
            setLegIncidents((prev) => {
                const next = new Map(prev);
                next.set(normalized.legIncidentId as number, normalized);
                return next;
            });
            // ALSO surface a screen-blocking alert (mirrors `panic`). De-dupe
            // by legIncidentId so a reconnect re-emit doesn't stack the same
            // incident on top of the queue.
            setIncidentQueue((q) => {
                if (normalized.legIncidentId != null && q.some((i) => i.legIncidentId === normalized.legIncidentId)) {
                    return q;
                }
                return [normalized, ...q];
            });
        });

        // Briefing complete — per-mission revision bump. The Mission
        // Engagements tab subscribes to it as a `useEffect` dep and
        // refetches the lifecycle envelope so "X of Y briefed" reflects
        // the latest stamp the moment the operator hits "I've read it".
        socket.on("briefing_completed", (event: BriefingCompleted) => {
            if (typeof event?.missionId !== "number") return;
            setBriefingRevision((prev) => {
                const next = new Map(prev);
                next.set(event.missionId, (prev.get(event.missionId) ?? 0) + 1);
                return next;
            });
        });

        // SitReps — push the id onto the per-mission list and bump
        // the revision counter. SitRepsTab subscribes to the revision
        // and re-fetches the bounded list endpoint so the row appears
        // within ~1s (replacing the legacy 30s polling). The id list
        // is mainly for diagnostics + a "burst detected" hint we can
        // wire later; the load-bearing signal is the revision counter.
        socket.on("sitrep_filed", (event: SitRepFiled) => {
            if (typeof event?.missionId !== "number") return;
            setSitRepsByMission((prev) => {
                const next = new Map(prev);
                const arr = next.get(event.missionId) ?? [];
                next.set(event.missionId, [...arr, event.sitRepId]);
                return next;
            });
            setSitRepRevision((prev) => {
                const next = new Map(prev);
                next.set(event.missionId, (prev.get(event.missionId) ?? 0) + 1);
                return next;
            });
            // Full-payload backlog (bounded, newest first) so the map's
            // SitRep-pin layer can plot severity + lat/lng without a
            // refetch. De-duped by sitRepId in case of reconnect re-emits.
            setSitRepEvents((prev) => {
                const arr = prev.get(event.missionId) ?? [];
                if (arr.some((s) => s.sitRepId === event.sitRepId)) return prev;
                const next = new Map(prev);
                next.set(event.missionId, [event, ...arr].slice(0, SITREP_BACKLOG_MAX));
                return next;
            });
        });

        // Live flight track fixes from the BE poller (adsb.lol / OpenSky).
        // Latest fix per flightInformationId wins — the map's plane marker
        // re-positions on every update; no backlog needed since only the
        // current position is meaningful.
        socket.on("flight_track_updated", (event: FlightTrackUpdate) => {
            if (typeof event?.flightInformationId !== "number") return;
            setFlightTracks((prev) => {
                const next = new Map(prev);
                next.set(event.flightInformationId, event);
                return next;
            });
        });

        // Expense reports — patch a map keyed by legExpenseId. The
        // Finance + Itinerary consumers layer this on top of their
        // page-mount fetch.
        socket.on("expense_filed", (event: ExpenseFiled) => {
            if (event.legExpenseId == null) return;
            setLegExpenses((prev) => {
                const next = new Map(prev);
                next.set(event.legExpenseId as number, event);
                return next;
            });
        });

        // Mission Debrief / AAR — per-mission revision bump. The
        // Debrief tab + the "X of Y debriefs filed" pill on Engagements
        // re-fetch on bump. Same revision-counter shape as
        // missionPhaseRevision so consumers can drop it straight into
        // a useEffect dep array.
        socket.on("debrief_filed", (event: DebriefFiled) => {
            if (typeof event?.missionId !== "number") return;
            setDebriefRevision((prev) => {
                const next = new Map(prev);
                next.set(event.missionId, (prev.get(event.missionId) ?? 0) + 1);
                return next;
            });
        });

        // World-incident alerts — content-less signal. Bump a monotonic
        // counter (Risk Console refetches on change) and stash the latest
        // payload so the map can show a transient "N new events" pill.
        socket.on("world_incident", (payload: WorldIncidentAlert) => {
            if (!payload || payload.kind !== "alerts") return;
            setLastWorldIncidentAlert({
                kind: "alerts",
                missionIds: Array.isArray(payload.missionIds) ? payload.missionIds : [],
                count: typeof payload.count === "number" ? payload.count : 0,
            });
            setWorldIncidentTick((prev) => prev + 1);
        });

        socket.on("entity:presence", (payload: { type: string; id: number; users: PresenceUser[] }) => {
            // One key per entity room — replacing the whole list each time
            // the server pushes is safe and keeps consumers' useMemo stable.
            setEntityPresence((prev) => {
                const next = new Map(prev);
                next.set(`${payload.type}:${payload.id}`, payload.users ?? []);
                return next;
            });
        });

        // Collaborative section edits — push-INVALIDATE. When another user
        // saves a section of a record someone else is viewing, the BE emits
        // this to the entity:{type}:{id} presence room. We bump a per-entity
        // revision counter; open detail pages watch it
        // (`useEntitySectionRevision`) and refetch the record through their own
        // permission-checked GET — no record body ever rides the socket, so a
        // finance=No viewer can't receive cost/price this way. Echo-guarded:
        // skip our own saves so a user is never told their own change is a
        // remote update (and doesn't trigger a redundant self-refetch).
        socket.on("entity:section-updated", (payload: { type?: string; id?: number | string; section?: string; updatedBy?: number | null }) => {
            if (!payload?.type || payload.id === undefined || payload.id === null) return;
            const selfUserId = getCurrentUserId();
            if (selfUserId != null && payload.updatedBy != null && Number(payload.updatedBy) === selfUserId) return;
            const key = `${payload.type}:${payload.id}`;
            setEntitySectionRevision((prev) => {
                const next = new Map(prev);
                next.set(key, (prev.get(key) ?? 0) + 1);
                return next;
            });
        });

        // Seed the buddy-overdue map with whatever's currently un-acked
        // server-side. The socket listener handles new events from this
        // point on; the seed handles "I just reloaded the page mid-event"
        // — without it the dispatcher's banner would be empty until the
        // next emit. Checklist + shift-report state isn't seeded here
        // because their consumers are mission-scoped and fetch on their
        // own page mount.
        (async () => {
            try {
                const resp = await getReq("missions/buddy-check-overdue/active");
                if (resp.status === 200 && Array.isArray(resp.body)) {
                    setBuddyOverdues((prev) => {
                        const next = new Map(prev);
                        for (const row of resp.body as BuddyOverdue[]) {
                            if (!row.acknowledgedAt) {
                                next.set(row.buddyCheckOverdueId, row);
                            }
                        }
                        return next;
                    });
                }
            } catch {
                // Non-fatal — banner just stays empty until a live event arrives.
            }
        })();

        // Comment thread events — one bounded backlog, newest first, each
        // stamped with a session-monotonic `seq`. Threads read their slice
        // via `useCommentsFeed` and apply add / replace / remove themselves
        // (the payload is the full CommentDTO, so no refetch per event).
        // `comment_updated` also fires on ack / pin / resolve.
        const pushCommentEvent = (type: CommentFeedEvent["type"]) => (
            payload: { comment?: { commentId?: number }; parentType?: string; parentId?: number; missionId?: number | null; at?: string },
        ) => {
            const commentId = payload?.comment?.commentId;
            if (typeof commentId !== "number" || !payload.parentType || typeof payload.parentId !== "number") return;
            commentSeqRef.current += 1;
            const event: CommentFeedEvent = {
                seq: commentSeqRef.current,
                type,
                comment: payload.comment as CommentFeedEvent["comment"],
                parentType: payload.parentType,
                parentId: payload.parentId,
                missionId: typeof payload.missionId === "number" ? payload.missionId : null,
                at: payload.at ?? new Date().toISOString(),
            };
            setCommentEvents((prev) => [event, ...prev].slice(0, COMMENT_BACKLOG_MAX));
        };
        socket.on("comment_added", pushCommentEvent("added"));
        socket.on("comment_updated", pushCommentEvent("updated"));
        socket.on("comment_deleted", pushCommentEvent("deleted"));

        // Re-emit entity:join (and editing-state, if any) every time the
        // socket re-establishes. The "connect" event fires for both the
        // initial connect and every reconnect, so this single handler
        // covers both. Without it, any dropped connection silently kicks
        // the user out of every entity room with no way back in.
        socket.on("connect", () => {
            setRealtimeConnectTick((prev) => prev + 1);
            for (const roomKey of Array.from(activeEntityRoomsRef.current)) {
                const sep = roomKey.indexOf(":");
                if (sep < 0) continue;
                const type = roomKey.slice(0, sep);
                const idStr = roomKey.slice(sep + 1);
                const idNum = Number(idStr);
                const id = Number.isFinite(idNum) ? idNum : (idStr as any);
                socket.emit("entity:join", { type, id });
                const isEditing = entityEditingRef.current.get(roomKey);
                if (isEditing) {
                    socket.emit("entity:editing", { type, id, isEditing: true });
                }
            }
        });

        return () => {
            socket.disconnect();
            socketRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.userId]);

    return (
        <RealtimeContext.Provider value={{
            panicQueue,
            acknowledgePanic,
            incidentQueue,
            acknowledgeIncident,
            panicEvents,
            sitRepEvents,
            flightTracks,
            operatorPositions,
            legCheckpoints,
            buddyOverdues,
            checklistSubmissions,
            shiftReports,
            riskAssessmentRevision,
            missionPhaseRevision,
            missionsListTick,
            missionPlanTick,
            missionCheckInRevision,
            legIncidents,
            briefingRevision,
            sitRepsByMission,
            sitRepRevision,
            legExpenses,
            debriefRevision,
            worldIncidentTick,
            lastWorldIncidentAlert,
            acknowledgeBuddyOverdue,
            entityPresence,
            entitySectionRevision,
            entityJoin,
            entityLeave,
            entityHeartbeat,
            entitySetEditing,
            commentEvents,
            realtimeConnectTick,
            realtimeStatus,
            ensureEntityRoom,
        }}>
            {children}
        </RealtimeContext.Provider>
    );
};

export const useRealtime = (): RealtimeContextValue => useContext(RealtimeContext);

/**
 * Lifecycle hook for an entity room. Joins on mount, leaves on unmount,
 * heartbeats every 30s while mounted. Pass isEditing to flip the user's
 * state (typically wired to your form's DirtyContext).
 *
 * Returns the deduped list of users currently in the room — one entry
 * per user, even if they have multiple tabs open. Excludes the current
 * user by default since the banner is meant to surface OTHERS.
 */
const HEARTBEAT_INTERVAL_MS = 30_000;

export const useEntityPresence = (
    type: string | null,
    id: number | null | undefined,
    isEditing: boolean = false,
    excludeSelfUserId: number | null = null,
): PresenceUser[] => {
    const { entityPresence, entityJoin, entityLeave, entityHeartbeat, entitySetEditing } = useRealtime();

    useEffect(() => {
        if (!type || !id) return;
        entityJoin(type, id);
        const interval = setInterval(() => entityHeartbeat(type, id), HEARTBEAT_INTERVAL_MS);
        return () => {
            clearInterval(interval);
            entityLeave(type, id);
        };
    }, [type, id, entityJoin, entityLeave, entityHeartbeat]);

    useEffect(() => {
        if (!type || !id) return;
        entitySetEditing(type, id, isEditing);
    }, [type, id, isEditing, entitySetEditing]);

    if (!type || !id) return [];
    const users = entityPresence.get(`${type}:${id}`) ?? [];
    if (excludeSelfUserId != null) {
        return users.filter(u => u.userId !== excludeSelfUserId);
    }
    return users;
};

/**
 * Returns the latest position for every operator currently broadcasting
 * on the given mission. Filters the global Map down to one mission so the
 * InteractiveMap consumer doesn't redraw when unrelated operators move.
 */
export const useOperatorPositions = (missionId: number | null | undefined): OperatorPosition[] => {
    const { operatorPositions } = useRealtime();
    if (missionId == null) return [];
    const out: OperatorPosition[] = [];
    operatorPositions.forEach((pos) => {
        if (pos.missionId === missionId) out.push(pos);
    });
    return out;
};

/**
 * Look up the latest checkpoint event (arrive / depart / complete) for
 * a specific leg. Returns undefined when no event has been broadcast
 * since the page loaded — the dispatcher view should fall back to the
 * persisted actualDateTimeFrom/To columns from the GET payload rather
 * than treating undefined as "no checkpoint".
 */
export const useLegCheckpoint = (legId: number | null | undefined): LegCheckpoint | undefined => {
    const { legCheckpoints } = useRealtime();
    if (legId == null) return undefined;
    return legCheckpoints.get(legId);
};

/**
 * @deprecated — use `useLegCheckpoint` instead. Kept temporarily so
 * stale call sites keep working through the FE migration; remove once
 * all consumers have switched.
 */
export const useItineraryCheckpoint = useLegCheckpoint;

/**
 * Active (un-acknowledged) buddy-overdue rows. Returned newest-first
 * by `triggeredAt` so the banner shows the most recent up top. The
 * Provider seeds this on mount via GET /mobile/buddy-check-overdue/active
 * AND keeps it in sync via the `buddy_check_overdue` socket event —
 * either way, this hook is the single read surface for the banner.
 */
export const useBuddyOverdues = (): BuddyOverdue[] => {
    const { buddyOverdues } = useRealtime();
    const out: BuddyOverdue[] = [];
    buddyOverdues.forEach((b) => {
        if (!b.acknowledgedAt) out.push(b);
    });
    out.sort((a, b) => (b.triggeredAt || "").localeCompare(a.triggeredAt || ""));
    return out;
};

/**
 * All checklist submissions on a specific leg, one per deployed
 * operator. Empty when no events have arrived and no page-mount fetch
 * has populated the cache yet — callers should treat that as
 * "PENDING". The leg card consumer should also fetch GET
 * /missions/:id/checklists on mount; this hook layers in live
 * `checklist_submitted` events so the badge re-aggregates without
 * a full page refresh.
 */
export const useChecklistForLeg = (legId: number | null | undefined): ChecklistSubmission[] => {
    const { checklistSubmissions } = useRealtime();
    if (legId == null) return [];
    const out: ChecklistSubmission[] = [];
    checklistSubmissions.forEach((sub) => {
        if (sub.legId === legId) out.push(sub);
    });
    return out;
};

/**
 * @deprecated — use `useChecklistForLeg` instead. Temporary alias for
 * stale call sites.
 */
export const useChecklistForItinerary = useChecklistForLeg;

/**
 * Live-only shift reports for a mission. Fed by the `shift_closed`
 * socket event. The mission's Shift Reports tab should still fetch
 * GET /mobile/missions/:id/shift-reports on mount for historical
 * rows; this hook contributes the "submitted while you have the page
 * open" updates. Filters the unscoped Map down to one mission so
 * unrelated shift events don't trigger re-renders.
 */
export const useShiftReportsForMission = (missionId: number | null | undefined): ShiftReport[] => {
    const { shiftReports } = useRealtime();
    if (missionId == null) return [];
    const out: ShiftReport[] = [];
    shiftReports.forEach((r) => {
        if (r.missionId === missionId) out.push(r);
    });
    return out;
};

/**
 * Monotonic revision number per mission. Bumped every time a
 * `risk_assessment_updated` or `risk_assessment_approved` socket event
 * fires for that mission. Consumers (RiskAssessmentTab + per-leg/site
 * findings panels + RiskPhotos) put this in a `useEffect` dependency
 * list so they re-fetch when anyone — mobile operator or another
 * dispatcher tab — edits the same assessment.
 */
export const useRiskAssessmentRevision = (missionId: number | null | undefined): number => {
    const { riskAssessmentRevision } = useRealtime();
    if (missionId == null) return 0;
    return riskAssessmentRevision.get(missionId) ?? 0;
};

/**
 * Monotonic revision number per mission, bumped on every
 * `mission_phase_changed` socket event. The Mission detail page's
 * MissionLifecyclePanel uses it as a `useEffect` dep to refetch the
 * envelope + check-in roster when a dispatcher in another tab — or
 * the mobile app's auto-suggest after the last operator checks in —
 * advances the mission's phase.
 */
export const useMissionPhaseRevision = (missionId: number | null | undefined): number => {
    const { missionPhaseRevision } = useRealtime();
    if (missionId == null) return 0;
    return missionPhaseRevision.get(missionId) ?? 0;
};

/**
 * Monotonic revision for a single entity-detail room (`${type}:${id}`),
 * bumped every time ANOTHER user saves a section of that record (own saves
 * are echo-guarded out server-payload-side). A detail page passes the same
 * (type, id) it gives EntityPresenceBanner and puts the returned number in a
 * useEffect dep: when it changes, refetch the record IF the form has no
 * unsaved edits, otherwise surface a non-destructive "updated by someone
 * else" prompt so the viewer's draft is never clobbered. Because the refetch
 * runs through the normal permission-checked GET, this is safe for
 * finance-restricted viewers — nothing sensitive travelled over the socket.
 *
 * `type` MUST match the entity-presence room string (e.g. "mission",
 * "userDetail", "organization", "missionRequest").
 */
export const useEntitySectionRevision = (type: string | null, id: number | null | undefined): number => {
    const { entitySectionRevision } = useRealtime();
    if (!type || id == null) return 0;
    return entitySectionRevision.get(`${type}:${id}`) ?? 0;
};

/**
 * Monotonic revision per mission, bumped on every operator check-in.
 * The MissionLifecyclePanel uses this in its load() effect so the
 * "X checked in / Y waiting" chips and the green→amber transition
 * buttons re-render the instant an operator confirms on mobile.
 */
export const useMissionCheckInRevision = (missionId: number | null | undefined): number => {
    const { missionCheckInRevision } = useRealtime();
    if (missionId == null) return 0;
    return missionCheckInRevision.get(missionId) ?? 0;
};

/**
 * Live `incident_filed` rows for a single leg. The page-mount fetch
 * supplies historical incidents; this hook layers on top whatever has
 * arrived over the socket since mount. Returns newest-first so the
 * consumer can prepend / merge without extra sorting. Empty when no
 * live events have arrived for the given leg.
 */
export const useIncidentsForLeg = (legId: number | null | undefined): IncidentFiled[] => {
    const { legIncidents } = useRealtime();
    if (legId == null) return [];
    const out: IncidentFiled[] = [];
    legIncidents.forEach((row) => {
        if (row.legId === legId) out.push(row);
    });
    out.sort((a, b) => (b.filedAt || "").localeCompare(a.filedAt || ""));
    return out;
};

/**
 * Monotonic revision per mission, bumped on every `briefing_completed`
 * socket event. Same shape as `useMissionPhaseRevision` so consumers
 * can drop it directly into a useEffect dep array and refetch the
 * Mission lifecycle envelope on bump.
 */
export const useBriefingRevision = (missionId: number | null | undefined): number => {
    const { briefingRevision } = useRealtime();
    if (missionId == null) return 0;
    return briefingRevision.get(missionId) ?? 0;
};

/**
 * Monotonic revision per mission, bumped on every `sitrep_filed`
 * socket event. The SitReps tab uses it as a useEffect dep so the
 * bounded list endpoint re-fetches the moment a new SitRep lands —
 * replacing the legacy 30s polling cadence. Critical SitReps surface
 * within ~1s of the operator hitting submit.
 */
export const useSitRepRevision = (missionId: number | null | undefined): number => {
    const { sitRepRevision } = useRealtime();
    if (missionId == null) return 0;
    return sitRepRevision.get(missionId) ?? 0;
};

/**
 * Non-destructive read of the panic events received this session for a
 * mission, newest first (bounded to the last 50 globally). Unlike
 * `panicQueue` (which the dispatcher drains by acknowledging), entries
 * stay here so map layers can keep a pulsing pin at each panic
 * location. Only rows for the given mission are returned; rows without
 * a GPS `location` are included — pin consumers filter those out.
 */
export const usePanicEventsForMission = (missionId: number | null | undefined): PanicAlert[] => {
    const { panicEvents } = useRealtime();
    if (missionId == null) return [];
    return panicEvents.filter((p) => p.missionId === missionId);
};

/**
 * Full SitRep payloads (severity + body + lat/lng) received live for a
 * mission since the page mounted, newest first, bounded to the last 50
 * per mission. Complements `useSitRepRevision` (refetch signal for the
 * SitReps tab) — this hook is the data source for the map's
 * severity-colored SitRep pins, which don't need the full persisted
 * list. Rows without coordinates are included; pin consumers skip them.
 */
export const useSitRepsForMission = (missionId: number | null | undefined): SitRepFiled[] => {
    const { sitRepEvents } = useRealtime();
    if (missionId == null) return [];
    return sitRepEvents.get(missionId) ?? [];
};

/**
 * Latest live track fix per flight on the given mission, fed by the
 * `flight_track_updated` socket event. Each entry carries `legId` so
 * the leg map can filter to its own leg without a new prop. Entries
 * with null lat/lng (scheduled / landed / not found) are returned too —
 * marker consumers plot only non-null fixes but may surface status.
 */
export const useFlightTracks = (missionId: number | null | undefined): FlightTrackUpdate[] => {
    const { flightTracks } = useRealtime();
    if (missionId == null) return [];
    const out: FlightTrackUpdate[] = [];
    flightTracks.forEach((t) => {
        if (t.missionId === missionId) out.push(t);
    });
    return out;
};

/**
 * Live `expense_filed` rows for a single leg. Same shape as
 * `useIncidentsForLeg` — page-mount fetch supplies historical
 * expenses, this hook layers in live ones. Newest-first by createdAt.
 */
export const useExpensesForLeg = (legId: number | null | undefined): ExpenseFiled[] => {
    const { legExpenses } = useRealtime();
    if (legId == null) return [];
    const out: ExpenseFiled[] = [];
    legExpenses.forEach((row) => {
        if (row.legId === legId) out.push(row);
    });
    out.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
    return out;
};

/**
 * Monotonic revision per mission, bumped on every `debrief_filed`
 * socket event. The Debrief tab + the "X of Y debriefs filed" pill on
 * Engagements re-fetch on bump.
 */
export const useDebriefRevision = (missionId: number | null | undefined): number => {
    const { debriefRevision } = useRealtime();
    if (missionId == null) return 0;
    return debriefRevision.get(missionId) ?? 0;
};

/**
 * Live world-incident alert signal. Returns the monotonic `tick` (bumped on
 * every `world_incident` socket event — use as a `useEffect` dep to refetch
 * incidents) plus the latest alert payload (count + missionIds) for the
 * transient "N new events" pill. The Risk Console consumes this; the
 * mission-side surfaces filter `lastAlert.missionIds` to their own mission.
 */
export const useWorldIncidentAlerts = (): { tick: number; lastAlert: WorldIncidentAlert | null } => {
    const { worldIncidentTick, lastWorldIncidentAlert } = useRealtime();
    return { tick: worldIncidentTick, lastAlert: lastWorldIncidentAlert };
};

/**
 * Comment events received this session for one thread, newest first
 * (bounded to the last 200 globally). Matches on `(parentType, parentId)`;
 * when `rollupMissionId` is given (the mission-level rollup thread) events
 * whose `missionId` matches are included too, so itinerary / leg comments
 * land in the mission thread live. The thread component de-dupes by
 * commentId and skips events it already applied via `seq`.
 */
export const useCommentsFeed = (
    parentType: string,
    parentId: number,
    rollupMissionId?: number,
): CommentFeedEvent[] => {
    const { commentEvents } = useRealtime();
    return useMemo(() => {
        if (!parentId && !rollupMissionId) return [];
        return commentEvents.filter((e) =>
            (e.parentType === parentType && e.parentId === parentId)
            || (rollupMissionId != null && e.missionId === rollupMissionId),
        );
    }, [commentEvents, parentType, parentId, rollupMissionId]);
};

/**
 * Socket connect counter — 0 until the first connect, +1 on every
 * (re)connect. Put it in a useEffect dep to run a catch-up fetch after a
 * dropped connection; skip the first change if the mount fetch already
 * covers it.
 */
export const useRealtimeConnectTick = (): number => {
    const { realtimeConnectTick } = useRealtime();
    return realtimeConnectTick;
};

/**
 * Keep the `entity:{type}:{id}` room joined while the caller is mounted,
 * without disturbing a page-level `useEntityPresence` on the same room
 * (ref-counted — see `ensureEntityRoom`). No presence list is returned;
 * this is for consumers that only need the room's broadcasts.
 */
export const useEnsureEntityRoom = (type: string | null, id: number | null | undefined): void => {
    const { ensureEntityRoom } = useRealtime();
    useEffect(() => {
        if (!type || !id) return;
        const release = ensureEntityRoom(type, id);
        return release;
    }, [type, id, ensureEntityRoom]);
};
